import { useMemo } from 'react';

import { useGetDocumentUploadData, useInitiateDocumentUpload, useSetUploadedDocumentUrl } from 'server';

export const useDocumentUploadPublicAPI = () => {
    const initiateDocumentUpload = useInitiateDocumentUpload();
    const setUploadedDocumentUrl = useSetUploadedDocumentUrl();
    const getDocumentUploadData = useGetDocumentUploadData();

    return useMemo(() => ({
        /**
         * Initiates document upload flow and returns a URL to be opened in an iframe / popup / new window.
         * @memberOf Playtech.API.documentUpload
         * @function
         * @param {Object} request
         * @param {string} [request.service]
         * @param {string} [request.successUrl]
         * @param {string} [request.errorUrl]
         * @param {string} [request.documentType]
         * @param {string} [request.remotePaymentAccountId]
         * @returns {Promise<{redirectURL: string, reference: string, sessionId: string, uploadCode: string, nationality: string, accessToken: string}>}
         * @example
         * Playtech.API.documentUpload.initiateDocumentUpload({
         *      errorUrl: 'https://error.com',
         *      documentType: 'PASSPORT',
         *      service: 'netverify',
         *      successUrl: 'https://success.com',
         *      remotePaymentAccountId: 'ePIl8ceR',
         * }).then(data => {...});
         */
        initiateDocumentUpload,
        /**
         * Stores a link to an externally stored document.
         * @memberOf Playtech.API.documentUpload
         * @function
         * @param {Object} request
         * @param {string} [request.URL]
         * @param {string} [request.documentId]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.documentUpload.setUploadedDocumentUrl({
         *     URL: 'https://example.com/image.jpg',
         *     documentId: '123'
         * }).then(() => {...});
         */
        setUploadedDocumentUrl,
        /**
         * Retrieves PII data about latest document upload of the player.
         * @memberOf Playtech.API.documentUpload
         * @function
         * @param {Object} request
         * @param {string} [request.reference]
         * @returns {Promise<{firstName: string, lastName: string}>}
         * @example
         * Playtech.API.documentUpload.getDocumentUploadData().then(() => {...});
         */
        getDocumentUploadData
    }), [
        getDocumentUploadData,
        initiateDocumentUpload,
        setUploadedDocumentUrl
    ]);
};
